/*!
 * Headroom.js (minimal, vendored)
 * Provides `window.Headroom` with pinned/unpinned/top state classes.
 */
(function () {
	'use strict';

	if (window.Headroom) {
		return;
	}

	function normalizeTolerance(tolerance) {
		if (typeof tolerance === 'number') {
			return { up: tolerance, down: tolerance };
		}
		if (!tolerance || typeof tolerance !== 'object') {
			return { up: 0, down: 0 };
		}
		return {
			up: typeof tolerance.up === 'number' ? tolerance.up : 0,
			down: typeof tolerance.down === 'number' ? tolerance.down : 0,
		};
	}

	function Headroom(element, options) {
		if (!element) {
			throw new Error('Headroom: element is required');
		}

		this.element = element;
		this.options = options || {};

		this.tolerance = normalizeTolerance(this.options.tolerance);
		this.offset = typeof this.options.offset === 'number' ? this.options.offset : 0;
		this.classes = Object.assign(
			{
				initial: 'headroom',
				pinned: 'headroom--pinned',
				unpinned: 'headroom--unpinned',
				top: 'headroom--top',
				notTop: 'headroom--not-top',
			},
			this.options.classes || {}
		);

		this.lastKnownScrollY = window.pageYOffset || 0;
		this.isPinned = true;
		this.isTop = true;
		this.frozen = false;
		this.rafId = 0;

		this.handleScroll = this.handleScroll.bind(this);
		this.update = this.update.bind(this);
	}

	Headroom.prototype.init = function () {
		this.element.classList.add(this.classes.initial, this.classes.pinned, this.classes.top);
		this.isPinned = true;
		this.isTop = true;
		this.lastKnownScrollY = window.pageYOffset || 0;

		window.addEventListener('scroll', this.handleScroll, { passive: true });
		return this;
	};

	Headroom.prototype.destroy = function () {
		window.removeEventListener('scroll', this.handleScroll);
		if (this.rafId) {
			window.cancelAnimationFrame(this.rafId);
			this.rafId = 0;
		}
		return this;
	};

	Headroom.prototype.freeze = function () {
		this.frozen = true;
		return this.pin();
	};

	Headroom.prototype.unfreeze = function () {
		this.frozen = false;
		this.lastKnownScrollY = window.pageYOffset || 0;
		this.handleScroll();
		return this;
	};

	Headroom.prototype.pin = function () {
		if (this.isPinned) {
			return this;
		}

		this.element.classList.remove(this.classes.unpinned);
		this.element.classList.add(this.classes.pinned);
		this.isPinned = true;
		return this;
	};

	Headroom.prototype.unpin = function () {
		if (!this.isPinned) {
			return this;
		}

		this.element.classList.remove(this.classes.pinned);
		this.element.classList.add(this.classes.unpinned);
		this.isPinned = false;
		return this;
	};

	Headroom.prototype.handleScroll = function () {
		if (this.rafId) {
			return;
		}

		this.rafId = window.requestAnimationFrame(this.update);
	};

	Headroom.prototype.update = function () {
		this.rafId = 0;

		if (this.frozen) {
			return;
		}

		var currentScrollY = window.pageYOffset || 0;
		var scrollDelta = currentScrollY - this.lastKnownScrollY;
		var scrollingDown = scrollDelta > 0;
		var scrollingUp = scrollDelta < 0;
		var pastOffset = currentScrollY > this.offset;

		if (pastOffset) {
			if (this.isTop) {
				this.element.classList.remove(this.classes.top);
				this.element.classList.add(this.classes.notTop);
				this.isTop = false;
			}
		} else if (!this.isTop) {
			this.element.classList.remove(this.classes.notTop);
			this.element.classList.add(this.classes.top);
			this.isTop = true;
		}

		if (!pastOffset) {
			this.pin();
			this.lastKnownScrollY = currentScrollY;
			return;
		}

		if (scrollingDown && Math.abs(scrollDelta) >= this.tolerance.down) {
			this.unpin();
		} else if (scrollingUp && Math.abs(scrollDelta) >= this.tolerance.up) {
			this.pin();
		}

		this.lastKnownScrollY = currentScrollY;
	};

	window.Headroom = Headroom;
})();

